// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React from 'react';
import { X, Rocket, AlertTriangle, CheckCircle, Search, Camera } from 'lucide-react';
import useFocusTrap from '../hooks/useFocusTrap';

const BetaWelcomeModal = ({ onClose, userName }) => {
  const trapRef = useFocusTrap({ active: true, onEscape: onClose });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-70 backdrop-blur-sm animate-in fade-in duration-300" role="dialog" aria-modal="true" aria-labelledby="welcome-modal-title">
      <div ref={trapRef} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden transform scale-100 transition-all">
        
        {/* Header - Fun & Exciting */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 text-white text-center relative">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white hover:bg-white/20 rounded-full p-1 transition"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 backdrop-blur-md">
            <Rocket className="w-8 h-8 text-white" />
          </div>
          <h2 id="welcome-modal-title" className="text-3xl font-bold mb-1">Welcome, PricePal Explorer! 🚀</h2>
          <p className="text-purple-100 font-medium">Your Daily Mission Briefing</p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* 1. The Mission */}
          <section className="bg-purple-50 border-l-4 border-purple-500 p-4 rounded-r-xl">
            <h3 className="text-lg font-bold text-purple-900 mb-2 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Your Mission
            </h3>
            <p className="text-purple-800 mb-2">
              You have a <strong>free lifetime account</strong> to help us test PricePal before it launches to the world.
            </p>
            <ul className="space-y-2 text-purple-700 text-sm">
              <li className="flex items-start gap-2">
                <span>🎯</span> <strong>Test Everything:</strong> Press buttons, try features, and see what happens.
              </li>
              <li className="flex items-start gap-2">
                <span>🛒</span> <strong>Scan Products:</strong> Teach the app new prices when you shop.
              </li>
              <li className="flex items-start gap-2">
                <span>🐛</span> <strong>Find Bugs:</strong> Be a hero—tell us if something breaks!
              </li>
            </ul>
          </section>

          {/* 2. Important Notice */}
          <section className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-xl">
            <h3 className="text-lg font-bold text-yellow-900 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Important: The "Empty" App
            </h3>
            <p className="text-yellow-800 text-sm">
              <strong>Why are there no prices yet?</strong><br/>
              Because we are brand new! The prices you see are ONLY from what you and other testers have scanned.
            </p>
            <p className="text-yellow-800 text-sm mt-2 font-semibold">
              If you search for "Bananas" and see nothing, YOU get to be the first person to scan them! 🍌
            </p>
          </section>

          {/* 3. How to Scan */}
          <section>
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Camera className="w-5 h-5 text-blue-600" />
              How to Add a Price
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center text-sm">
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <div className="text-2xl mb-1">📷</div>
                <div className="font-bold text-gray-800">1. Tap Scan</div>
                <div className="text-gray-500">Use the big camera button</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <div className="text-2xl mb-1">📦</div>
                <div className="font-bold text-gray-800">2. Scan Barcode</div>
                <div className="text-gray-500">Point at the product code</div>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                <div className="text-2xl mb-1">💰</div>
                <div className="font-bold text-gray-800">3. Enter Price</div>
                <div className="text-gray-500">Type the price & store</div>
              </div>
            </div>
          </section>

          {/* 4. Reporting Bugs */}
          <section className="bg-blue-50 rounded-xl p-4 text-center">
            <h3 className="text-md font-bold text-blue-900 mb-1">See something weird?</h3>
            <p className="text-blue-700 text-sm mb-3">
              Go to <strong>More {'>'} Help & Support {'>'} Report a Bug</strong>
            </p>
            <p className="text-xs text-blue-600 italic">
              "No question is too silly to ask!"
            </p>
          </section>

        </div>

        {/* Footer Action */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white text-lg font-bold py-4 rounded-xl shadow-lg hover:from-green-600 hover:to-emerald-700 hover:scale-[1.02] transition-all transform flex items-center justify-center gap-2"
          >
            <span>🚀</span>
            I Accept the Mission!
          </button>
        </div>

      </div>
    </div>
  );
};

export default BetaWelcomeModal;
